import { AppSelect } from '../components/ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  Clipboard,
  Clock,
  FileJson,
  Film,
  Images,
  Loader2,
  Play,
  RefreshCw,
  UploadCloud,
  Video,
  XCircle,
} from 'lucide-react';
import { GLOBAL_AI_PROVIDERS, normalizeGlobalProvider } from '../constants/globalAiProviders';
import { ASTRAFLOW_ASTRA_MODELS } from '../constants/astraflowAstraModels';
import { useToast } from '../context/ToastContext';
import {
  fetchReferenceVideoReverseList,
  fetchReferenceVideoReverseStatus,
  submitReferenceVideoReverse,
} from '../services/referenceVideoReverseService';

const STATUS_META = {
  pending: { label: '排队中', color: 'var(--color-warning)', icon: Clock },
  processing: { label: '处理中', color: 'var(--color-warning)', icon: Loader2 },
  success: { label: '已完成', color: 'var(--color-success)', icon: CheckCircle2 },
  failed: { label: '失败', color: 'var(--color-error)', icon: XCircle },
};

const VISION_PROVIDER_IDS = new Set([
  'wangsugem',
  'wangsugpt',
  'huobao',
  'huobao_gpt',
  'astraflow_gemini',
  'astraflow_openai',
  'astraflow_openai_luna',
  'astraflow_openai_terra',
  'astraflow_openai_sol',
  ...ASTRAFLOW_ASTRA_MODELS.map(({ id }) => id),
  'claude_opus_mj',
]);

const VISION_PROVIDERS = GLOBAL_AI_PROVIDERS.filter((item) => VISION_PROVIDER_IDS.has(item.id));

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (!value) return '-';
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
};

const pretty = (value) => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value ?? null, null, 2);
  } catch {
    return String(value ?? '');
  }
};

const EXTRACTION_MODE_LABELS = {
  fixed: '定数抽帧',
  dynamic: '动态场景',
  uniform: '等时抽帧',
  keyframe: '关键帧',
  scene: '场景切换',
};

const extractSegments = (task) => {
  const payload = task?.segments;
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.segments)) return payload.segments;
  return [];
};

const extractAnalysisPayload = (task) => {
  const payload = task?.segments;
  if (!payload) return {};
  if (Array.isArray(payload)) return { segments: payload };
  return payload;
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium"
      style={{
        color: meta.color,
        borderColor: `color-mix(in srgb, ${meta.color} 33%, transparent)`,
        backgroundColor: `color-mix(in srgb, ${meta.color} 11%, transparent)`,
      }}
    >
      <Icon size={13} className={status === 'processing' ? 'animate-spin' : ''} />
      {meta.label}
    </span>
  );
};

const ReferenceVideoReversePage = () => {
  const routeParams = useParams();
  const routeProjectId = routeParams.projectId || '';
  const toast = useToast();
  const pollRef = useRef(null);
  const [file, setFile] = useState(null);
  const [projectId, setProjectId] = useState(routeProjectId);
  const [episodeId, setEpisodeId] = useState('');
  const [provider, setProvider] = useState(() => {
    const normalized = normalizeGlobalProvider(localStorage.getItem('ai_provider') || 'wangsugem');
    return VISION_PROVIDER_IDS.has(normalized) ? normalized : 'wangsugem';
  });
  const [extractionMode, setExtractionMode] = useState('fixed');
  const [viewMode, setViewMode] = useState('timeline');
  const [editableSegments, setEditableSegments] = useState([]);
  const [maxFrames, setMaxFrames] = useState(24);
  const [samplingIntervalSeconds, setSamplingIntervalSeconds] = useState(3);
  const [submitting, setSubmitting] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [history, setHistory] = useState([]);

  const analysisPayload = useMemo(() => extractAnalysisPayload(currentTask), [currentTask]);
  const segments = useMemo(() => extractSegments(currentTask), [currentTask]);
  const running = currentTask && ['pending', 'processing'].includes(currentTask.status);

  useEffect(() => {
    setEditableSegments(segments.map((segment, index) => ({
      segmentIndex: segment.segmentIndex || index + 1,
      title: segment.title || '',
      timeRange: segment.timeRange || '',
      durationSeconds: segment.durationSeconds || '',
      cutReason: segment.cutReason || '',
      visualSummary: segment.visualSummary || '',
      camera: segment.camera || '',
      movement: segment.movement || '',
      lighting: segment.lighting || '',
      transition: segment.transition || '',
      spatial: segment.spatial || {},
      shots: Array.isArray(segment.shots) ? segment.shots : [],
      seedancePromptDraft: segment.seedancePromptDraft || '',
    })));
  }, [segments]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refreshTask = useCallback(async (taskId) => {
    if (!taskId) return null;
    const data = await fetchReferenceVideoReverseStatus(taskId);
    setCurrentTask(data);
    if (!['pending', 'processing'].includes(data?.status)) {
      stopPolling();
    }
    return data;
  }, [stopPolling]);

  const startPolling = useCallback((taskId) => {
    stopPolling();
    pollRef.current = window.setInterval(() => {
      refreshTask(taskId).catch((error) => {
        stopPolling();
        toast.error(`刷新反拆任务失败: ${error.message}`);
      });
    }, 2200);
  }, [refreshTask, stopPolling, toast]);

  const loadHistory = useCallback(async () => {
    try {
      const list = await fetchReferenceVideoReverseList({ projectId: projectId || undefined });
      setHistory(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error(`加载历史失败: ${error.message}`);
    }
  }, [projectId, toast]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleSubmit = async () => {
    if (!file) {
      toast.warning('先选择一个参考视频');
      return;
    }
    setSubmitting(true);
    try {
      const task = await submitReferenceVideoReverse({
        file,
        projectId: projectId || undefined,
        episodeId: episodeId || undefined,
        provider,
        maxFrames,
        samplingIntervalSeconds,
        extractionMode,
      });
      setCurrentTask(task);
      startPolling(task.taskId);
      loadHistory();
      toast.success('参考视频反拆任务已提交');
    } catch (error) {
      toast.error(`提交失败: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const updateEditableSegment = (index, key, value) => {
    setEditableSegments(prev => prev.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [key]: value } : item
    )));
  };

  const exportAnalysis = () => {
    const payload = {
      taskId: currentTask?.taskId,
      originalFilename: currentTask?.originalFilename,
      videoMetadata: currentTask?.videoMetadata,
      extractionMode: currentTask?.extractionMode,
      contactSheetUrl: currentTask?.contactSheetUrl,
      frames: currentTask?.frames || [],
      overall: analysisPayload?.overall || null,
      segments: editableSegments,
      rawAnalysis: currentTask?.rawAnalysis,
    };
    copyText(pretty(payload), '反拆结果 JSON');
  };

  const copyText = async (value, label = '内容') => {
    try {
      await navigator.clipboard.writeText(value || '');
      toast.success(`${label}已复制`);
    } catch {
      toast.error('复制失败');
    }
  };

  return (
    <div className="min-h-screen overflow-auto px-6 py-6" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}>
      <div className="mx-auto flex max-w-[1800px] flex-col gap-5">
        <header className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border p-5" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}>
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs" style={{ borderColor: 'var(--color-accent-primary)', color: 'var(--color-accent-primary)', backgroundColor: 'var(--color-accent-light)' }}>
              <Film size={14} />
              独立测试功能
            </div>
            <h1 className="text-2xl font-semibold">参考视频反拆</h1>
            <p className="mt-2 max-w-3xl text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              上传参考视频后，后端用 ffmpeg 抽取关键帧，生成联系表，再通过视觉模型反推出镜头切点、人物站位、景别变化和视频草稿提示词。
            </p>
          </div>
          <button
            type="button"
            onClick={loadHistory}
            className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm"
            style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-tertiary)' }}
          >
            <RefreshCw size={16} />
            刷新历史
          </button>
        </header>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[380px_minmax(0,1fr)_360px]">
          <section className="rounded-2xl border" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}>
            <div className="border-b p-4" style={{ borderColor: 'var(--color-border-primary)' }}>
              <h2 className="flex items-center gap-2 font-semibold">
                <UploadCloud size={18} style={{ color: 'var(--color-accent-primary)' }} />
                上传与参数
              </h2>
            </div>
            <div className="space-y-4 p-4">
              <label
                className="flex min-h-[170px] cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border border-dashed px-4 py-6 text-center"
                style={{ borderColor: 'var(--color-border-secondary)', backgroundColor: 'var(--color-bg-tertiary)' }}
              >
                <Video size={34} style={{ color: 'var(--color-accent-primary)' }} />
                <div className="text-sm font-medium">{file ? file.name : '选择 mp4/mov/webm 等参考视频'}</div>
                <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {file ? formatBytes(file.size) : '默认限制 100MB；单次最多抽 48 帧'}
                </div>
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/x-m4v,video/x-msvideo,video/x-matroska"
                  className="hidden"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  Project ID
                  <input
                    value={projectId}
                    onChange={(event) => setProjectId(event.target.value)}
                    placeholder="可空"
                    className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                    style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                  />
                </label>
                <label className="space-y-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  Episode ID
                  <input
                    value={episodeId}
                    onChange={(event) => setEpisodeId(event.target.value)}
                    placeholder="可空"
                    className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                    style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                  />
                </label>
              </div>

              <label className="space-y-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                视觉分析模型
                <AppSelect
                  value={provider}
                  onChange={(event) => setProvider(event.target.value)}
                  className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                  style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                >
                  {VISION_PROVIDERS.map((item) => (
                    <option key={item.id} value={item.id}>{item.name} · {item.recommendation}</option>
                  ))}
                </AppSelect>
              </label>

              <label className="space-y-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                抽帧模式
                <AppSelect
                  value={extractionMode}
                  onChange={(event) => setExtractionMode(event.target.value)}
                  className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                  style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                >
                  <option value="fixed">定数抽帧：按视频时长反推间隔，覆盖整条视频</option>
                  <option value="dynamic">动态场景：低阈值识别画面变化，适合动作和复杂剪辑</option>
                  <option value="uniform">等时抽帧：固定每隔 N 秒抽一帧</option>
                  <option value="keyframe">关键帧：优先抽编码关键画面</option>
                  <option value="scene">场景切换：优先找画面变化点</option>
                </AppSelect>
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  目标/上限画面数
                  <input
                    type="number"
                    min={4}
                    max={48}
                    value={maxFrames}
                    onChange={(event) => setMaxFrames(Number(event.target.value))}
                    className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                    style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                  />
                </label>
                <label className="space-y-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  等时抽帧间隔
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={samplingIntervalSeconds}
                    disabled={extractionMode !== 'uniform'}
                    onChange={(event) => setSamplingIntervalSeconds(Number(event.target.value))}
                    className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                    style={{
                      borderColor: 'var(--color-border-primary)',
                      backgroundColor: extractionMode === 'uniform' ? 'var(--color-bg-primary)' : 'var(--color-bg-tertiary)',
                      color: extractionMode === 'uniform' ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                    }}
                  />
                </label>
              </div>

              <div className="rounded-xl border p-3 text-xs leading-6" style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-primary)' }}>
                {extractionMode === 'fixed' && '定数抽帧会读取视频时长，把目标画面数均匀分布到整条视频上。'}
                {extractionMode === 'uniform' && '等时抽帧会从开头开始每隔 N 秒抽 1 帧，直到达到上限；长视频可能只覆盖开头部分。'}
                {extractionMode === 'dynamic' && '动态场景会按画面变化抽帧，画面变化越多越容易接近上限。'}
                {extractionMode === 'scene' && '场景切换会用更高阈值抽明显切点，适合剪辑节奏清晰的视频。'}
                {extractionMode === 'keyframe' && '关键帧模式依赖视频编码关键帧，适合快速抓取代表画面，但时间不一定均匀。'}
              </div>

              <button
                type="button"
                disabled={submitting || running}
                onClick={handleSubmit}
                className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-[var(--color-accent-contrast)] disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-accent-primary)' }}
              >
                {submitting || running ? <Loader2 size={17} className="animate-spin" /> : <Play size={17} />}
                {running ? '反拆中' : '开始反拆'}
              </button>

              <div className="rounded-xl border p-3 text-xs leading-6" style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}>
                第一版只做反拆分析，不会自动创建小故事板片段，也不会生成视频。后续确认结构后再接入导入动作。
              </div>
            </div>
          </section>

          <section className="min-w-0 rounded-2xl border" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4" style={{ borderColor: 'var(--color-border-primary)' }}>
              <div>
                <h2 className="font-semibold">反拆结果</h2>
                <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {currentTask?.taskId || '提交任务后这里显示抽帧和结构化结果'}
                </p>
              </div>
              {currentTask && <StatusBadge status={currentTask.status} />}
            </div>

            {!currentTask ? (
              <div className="flex min-h-[520px] flex-col items-center justify-center gap-3 text-center" style={{ color: 'var(--color-text-muted)' }}>
                <Images size={44} />
                <div className="text-sm">等待上传参考视频</div>
              </div>
            ) : (
              <div className="space-y-5 p-4">
                <div className="rounded-xl border p-4" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)' }}>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium">{currentTask.stageText || '处理中'}</div>
                      <div className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        {currentTask.originalFilename} · {formatBytes(currentTask.videoSizeBytes)} · {currentTask.frameCount || 0} 帧 · {EXTRACTION_MODE_LABELS[currentTask.extractionMode] || '定数抽帧'}
                      </div>
                    </div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--color-accent-primary)' }}>
                      {currentTask.progress || 0}%
                    </div>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${currentTask.progress || 0}%`, backgroundColor: 'var(--color-accent-primary)' }}
                    />
                  </div>
                  {currentTask.errorMessage && (
                    <div className="mt-3 flex gap-2 rounded-xl border p-3 text-sm" style={{ borderColor: 'color-mix(in srgb, var(--color-error) 35%, transparent)', color: 'var(--color-error)', backgroundColor: 'var(--color-red-light)' }}>
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{currentTask.errorMessage}</span>
                    </div>
                  )}
                </div>

                {currentTask.videoMetadata && Object.keys(currentTask.videoMetadata).length > 0 && (
                  <div className="rounded-xl border p-4" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)' }}>
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-semibold">视频元数据</h3>
                      <button type="button" onClick={() => copyText(pretty(currentTask.videoMetadata), '视频元数据')} className="text-xs" style={{ color: 'var(--color-accent-primary)' }}>复制</button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs md:grid-cols-4">
                      <div className="p-3">
                        <div style={{ color: 'var(--color-text-muted)' }}>时长</div>
                        <div className="mt-1 font-semibold">{Number(currentTask.videoMetadata?.format?.duration || 0).toFixed(1)}s</div>
                      </div>
                      <div className="p-3">
                        <div style={{ color: 'var(--color-text-muted)' }}>格式</div>
                        <div className="mt-1 truncate font-semibold">{currentTask.videoMetadata?.format?.format_name || '-'}</div>
                      </div>
                      <div className="p-3">
                        <div style={{ color: 'var(--color-text-muted)' }}>分辨率</div>
                        <div className="mt-1 font-semibold">
                          {(() => {
                            const stream = currentTask.videoMetadata?.streams?.find?.((item) => item.codec_type === 'video');
                            return stream ? `${stream.width || '-'} x ${stream.height || '-'}` : '-';
                          })()}
                        </div>
                      </div>
                      <div className="p-3">
                        <div style={{ color: 'var(--color-text-muted)' }}>编码</div>
                        <div className="mt-1 truncate font-semibold">
                          {currentTask.videoMetadata?.streams?.find?.((item) => item.codec_type === 'video')?.codec_name || '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentTask.contactSheetUrl && (
                  <div className="rounded-xl border p-4" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)' }}>
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-semibold">抽帧联系表</h3>
                      <a href={currentTask.contactSheetUrl} target="_blank" rel="noreferrer" className="text-xs" style={{ color: 'var(--color-accent-primary)' }}>打开原图</a>
                    </div>
                    <img
                      src={currentTask.contactSheetUrl}
                      alt="参考视频抽帧联系表"
                      className="max-h-[520px] w-full rounded-xl object-contain"
                      style={{ backgroundColor: 'var(--color-bg-overlay)' }}
                    />
                  </div>
                )}

                {!!currentTask.frames?.length && (
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {currentTask.frames.map((frame) => (
                      <a key={frame.index} href={frame.url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-xl border" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)' }}>
                        <img src={frame.url} alt={`frame-${frame.index}`} className="aspect-video w-full object-cover" />
                        <div className="px-2 py-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          #{frame.index} · ~{frame.approxSecond}s
                        </div>
                      </a>
                    ))}
                  </div>
                )}

                {analysisPayload?.overall && (
                  <div className="rounded-xl border p-4" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)' }}>
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-semibold">整体反拆结论</h3>
                      <button type="button" onClick={() => copyText(pretty(analysisPayload.overall), '整体反拆结论')} className="text-xs" style={{ color: 'var(--color-accent-primary)' }}>复制</button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 text-xs md:grid-cols-2">
                      {[
                        ['sceneSummary', '场景/题材'],
                        ['visualStyle', '画面风格'],
                        ['lightingTone', '光线基调'],
                        ['continuityNotes', '连续性提示'],
                      ].map(([key, label]) => (
                        <div key={key} className="p-3 leading-5">
                          <div className="mb-1 font-medium" style={{ color: 'var(--color-text-muted)' }}>{label}</div>
                          <div style={{ color: 'var(--color-text-secondary)' }}>{analysisPayload.overall?.[key] || '-'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {editableSegments.length > 0 && (
                  <div className="rounded-xl border" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)' }}>
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--color-border-primary)' }}>
                      <h3 className="text-sm font-semibold">可编辑分镜拆解</h3>
                      <div className="flex flex-wrap items-center gap-2">
                        {['timeline', 'grid', 'list'].map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            onClick={() => setViewMode(mode)}
                            className="rounded-lg border px-3 py-1.5 text-xs"
                            style={{
                              borderColor: viewMode === mode ? 'var(--color-accent-primary)' : 'var(--color-border-primary)',
                              color: viewMode === mode ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)',
                              backgroundColor: viewMode === mode ? 'var(--color-accent-light)' : 'transparent',
                            }}
                          >
                            {mode === 'timeline' ? '时间线' : mode === 'grid' ? '网格' : '列表'}
                          </button>
                        ))}
                        <button type="button" onClick={exportAnalysis} className="rounded-lg border px-3 py-1.5 text-xs" style={{ borderColor: 'var(--color-accent-primary)', color: 'var(--color-accent-primary)' }}>导出 JSON</button>
                      </div>
                    </div>
                    <div key={viewMode} className={`animate-in fade-in duration-150 ${viewMode === 'grid' ? 'grid grid-cols-1 gap-3 p-4 lg:grid-cols-2' : 'space-y-3 p-4'}`}>
                      {editableSegments.map((segment, index) => (
                        <div
                          key={`${segment.segmentIndex}-${index}`}
                          className="rounded-xl border p-4"
                          style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}
                        >
                          <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-[80px_1fr_140px]">
                            <input
                              value={`#${segment.segmentIndex}`}
                              readOnly
                              className="rounded-lg border px-3 py-2 text-xs outline-none"
                              style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-muted)' }}
                            />
                            <input
                              value={segment.title}
                              onChange={(event) => updateEditableSegment(index, 'title', event.target.value)}
                              placeholder="片段标题"
                              className="rounded-lg border px-3 py-2 text-sm outline-none"
                              style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                            />
                            <input
                              value={segment.timeRange}
                              onChange={(event) => updateEditableSegment(index, 'timeRange', event.target.value)}
                              placeholder="00:00-00:12"
                              className="rounded-lg border px-3 py-2 text-xs outline-none"
                              style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                            />
                          </div>
                          <textarea
                            value={segment.visualSummary}
                            onChange={(event) => updateEditableSegment(index, 'visualSummary', event.target.value)}
                            rows={viewMode === 'list' ? 2 : 3}
                            placeholder="画面概述"
                            className="mb-3 w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none"
                            style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                          />
                          <div className="grid grid-cols-1 gap-3 text-xs md:grid-cols-3">
                            {[
                              ['cutReason', '切点原因'],
                              ['camera', '机位/景别'],
                              ['movement', '运动/调度'],
                              ['lighting', '光线基调'],
                              ['transition', '转场/节拍'],
                            ].map(([key, label]) => (
                              <label key={key} className={key === 'lighting' || key === 'transition' ? 'md:col-span-3' : ''} style={{ color: 'var(--color-text-muted)' }}>
                                {label}
                                <input
                                  value={segment[key] || ''}
                                  onChange={(event) => updateEditableSegment(index, key, event.target.value)}
                                  className="mt-1 w-full rounded-lg border px-3 py-2 text-xs outline-none"
                                  style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                                />
                              </label>
                            ))}
                          </div>
                          {(segment.spatial && Object.keys(segment.spatial).length > 0) && (
                            <div className="mt-3 grid grid-cols-1 gap-2 text-xs md:grid-cols-3">
                              {[
                                ['characters', '人物位置'],
                                ['sceneAnchors', '场景锚点'],
                                ['screenDirection', '视线/运动方向'],
                              ].map(([key, label]) => (
                                <div key={key} className="rounded-lg p-3 leading-5" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)' }}>
                                  <div className="mb-1 font-medium" style={{ color: 'var(--color-text-muted)' }}>{label}</div>
                                  {segment.spatial?.[key] || '-'}
                                </div>
                              ))}
                            </div>
                          )}
                          {Array.isArray(segment.shots) && segment.shots.length > 0 && (
                            <div className="mt-3 rounded-lg border" style={{ borderColor: 'var(--color-border-primary)' }}>
                              <div className="border-b px-3 py-2 text-xs font-semibold" style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)' }}>镜头分解</div>
                              <div className="divide-y" style={{ borderColor: 'var(--color-border-primary)' }}>
                                {segment.shots.map((shot, shotIndex) => (
                                  <div key={`${shot.shotIndex || shotIndex}-${shot.image || ''}`} className="grid grid-cols-1 gap-2 px-3 py-2 text-xs leading-5 md:grid-cols-[70px_minmax(0,1fr)]" style={{ borderColor: 'var(--color-border-primary)' }}>
                                    <div style={{ color: 'var(--color-text-muted)' }}>镜头 {shot.shotIndex || shotIndex + 1}</div>
                                    <div className="space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                                      <div>{shot.image || '-'}</div>
                                      <div style={{ color: 'var(--color-text-muted)' }}>{shot.cameraMove || ''}{shot.beat ? ` · ${shot.beat}` : ''}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <textarea
                            value={segment.seedancePromptDraft}
                            onChange={(event) => updateEditableSegment(index, 'seedancePromptDraft', event.target.value)}
                            rows={viewMode === 'list' ? 3 : 5}
                            placeholder="视频生成草稿提示词"
                            className="mt-3 w-full resize-y rounded-lg border px-3 py-2 text-xs leading-5 outline-none"
                            style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-xl border" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)' }}>
                  <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: 'var(--color-border-primary)' }}>
                    <h3 className="flex items-center gap-2 text-sm font-semibold"><FileJson size={16} />原始分析</h3>
                    <button type="button" onClick={() => copyText(currentTask.rawAnalysis || '', '原始分析')} className="text-xs" style={{ color: 'var(--color-accent-primary)' }}>复制</button>
                  </div>
                  <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap p-4 text-xs leading-6" style={{ color: 'var(--color-text-secondary)' }}>
                    {currentTask.rawAnalysis || '暂无'}
                  </pre>
                </div>
              </div>
            )}
          </section>

          <aside className="rounded-2xl border" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}>
            <div className="border-b p-4" style={{ borderColor: 'var(--color-border-primary)' }}>
              <h2 className="font-semibold">片段草稿</h2>
              <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>用于评估切点、镜头和提示词质量。</p>
            </div>
            <div className="max-h-[calc(100vh-180px)] space-y-3 overflow-auto p-4">
              {segments.length === 0 && (
                <div className="rounded-xl border p-4 text-sm" style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}>
                  暂无片段结果
                </div>
              )}
              {segments.map((segment, index) => (
                <div key={`${segment.segmentIndex || index}-${segment.title || ''}`} className="rounded-xl border p-3" style={{ borderColor: 'var(--color-border-primary)' }}>
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold">#{segment.segmentIndex || index + 1} {segment.title || '未命名片段'}</div>
                      <div className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>{segment.timeRange || '-'} · {segment.durationSeconds || '-'}s</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyText(segment.seedancePromptDraft || pretty(segment), '片段提示词')}
                      className="rounded-lg border p-1.5"
                      style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                      title="复制提示词"
                    >
                      <Clipboard size={14} />
                    </button>
                  </div>
                  <div className="space-y-2 text-xs leading-5" style={{ color: 'var(--color-text-secondary)' }}>
                    <p><span style={{ color: 'var(--color-text-muted)' }}>切点：</span>{segment.cutReason || '-'}</p>
                    <p><span style={{ color: 'var(--color-text-muted)' }}>画面：</span>{segment.visualSummary || '-'}</p>
                    <p><span style={{ color: 'var(--color-text-muted)' }}>镜头：</span>{segment.camera || '-'}</p>
                    <p><span style={{ color: 'var(--color-text-muted)' }}>运动：</span>{segment.movement || '-'}</p>
                    <p><span style={{ color: 'var(--color-text-muted)' }}>打光：</span>{segment.lighting || '-'}</p>
                  </div>
                  {segment.seedancePromptDraft && (
                    <div className="mt-3 rounded-lg p-3 text-xs leading-5" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
                      {segment.seedancePromptDraft}
                    </div>
                  )}
                </div>
              ))}

              <div className="pt-3">
                <h3 className="mb-2 text-sm font-semibold">历史任务</h3>
                <div className="space-y-2">
                  {history.map((item) => (
                    <button
                      type="button"
                      key={item.taskId}
                      onClick={() => {
                        setCurrentTask(item);
                        if (['pending', 'processing'].includes(item.status)) startPolling(item.taskId);
                      }}
                      className="w-full rounded-xl border p-3 text-left text-xs"
                      style={{ borderColor: currentTask?.taskId === item.taskId ? 'var(--color-accent-primary)' : 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)' }}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="truncate font-medium">{item.originalFilename || item.taskId}</span>
                        <StatusBadge status={item.status} />
                      </div>
                      <div style={{ color: 'var(--color-text-muted)' }}>{item.taskId}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ReferenceVideoReversePage;
